/**
 * ReportHero — W2.2 / Contract MR-v1
 *
 * Zeigt den KPI-Strip des Monatsreports:
 *   Brutto HNT · Provisionen HNT · Netto HNT · EUR-Gegenwert (@ avg-rate)
 * sowie darunter die Token-Totals (HNT/IOT/MOBILE), nur Tokens mit Wert > 0.
 */
import { useTranslation } from 'react-i18next'
import type { MonthlyReport } from './useMonthlyReport'
import { formatCurrency } from './reportUtils'

const TOKEN_COLORS = {
  hnt: 'var(--token-hnt,    #474DFF)',
  iot: 'var(--token-iot,    #21C5A0)',
  mobile: 'var(--token-mobile, #009FF9)',
}

type Props = {
  report: MonthlyReport
}

// --- KPI-Karte (HNT-Werte mit 4 Dezimalstellen) ---

type KpiProps = {
  label: string
  value: string
  hint?: string
}

function Kpi({ label, value, hint }: KpiProps) {
  return (
    <div className="report-kpi" aria-label={`${label}: ${value}`}>
      <div className="report-kpi-label">{label}</div>
      <div className="num-lg report-kpi-value" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
      {hint && <div className="report-kpi-hint muted">{hint}</div>}
    </div>
  )
}

// --- Token-Total (Sekundaer-Zeile) ---

type TokenTotalProps = {
  label: string
  value: number
  color: string
}

function TokenTotal({ label, value, color }: TokenTotalProps) {
  return (
    <div className="report-hero-token" aria-label={`${label}: ${value.toFixed(4)}`}>
      <div className="report-hero-token-label" style={{ color }}>
        {label}
      </div>
      <div
        className="num-lg report-hero-token-value"
        style={{ color, fontVariantNumeric: 'tabular-nums' }}
      >
        {value.toFixed(4)}
      </div>
    </div>
  )
}

export function ReportHero({ report }: Props) {
  const { t } = useTranslation('reports')
  const { summary } = report

  const tokens = [
    { label: 'HNT', value: report.total_hnt, color: TOKEN_COLORS.hnt },
    { label: 'IOT', value: report.total_iot, color: TOKEN_COLORS.iot },
    { label: 'MOBILE', value: report.total_mobile, color: TOKEN_COLORS.mobile },
  ].filter((tok) => tok.value > 0)

  // P2 (Design-Audit 2026-09-01): Die Kopfzeile "N Hotspots generated ..." +
  // "Period: ..." (R3/FE-6) ist entfernt — die Design-Vorlage
  // (monthly-report.html) zeigt unter dem Titel nur Monatsauswahl + PDF-Button,
  // direkt gefolgt von der KPI-Karte. Periode ist bereits im Monats-Select im
  // Header sichtbar, "N Hotspots" ist redundant zur Tabelle darunter.

  return (
    <div className="report-hero-wrap" aria-label={t('hero.ariaLabel')}>
      <div className="card report-kpi-strip" aria-label={t('hero.kpiStrip')}>
        <Kpi label={t('hero.grossHnt')} value={summary.gross_rewards_hnt.toFixed(2)} />
        <Kpi label={t('hero.commissionsHnt')} value={summary.commissions_paid_hnt.toFixed(2)} />
        <Kpi label={t('hero.netHnt')} value={summary.net_rewards_hnt.toFixed(2)} />
        <Kpi
          label={t('hero.value', { currency: summary.currency })}
          value={formatCurrency(summary.total_rewards_fiat, summary.currency)}
          hint={t('hero.valueHint', {
            rate: formatCurrency(summary.avg_hnt_fiat_rate, summary.currency),
          })}
        />
      </div>

      {tokens.length > 0 && (
        <div className="card card-pad report-hero" aria-label={t('hero.totals')}>
          {tokens.map((tok) => (
            <TokenTotal key={tok.label} {...tok} />
          ))}
        </div>
      )}
    </div>
  )
}
